import type { PropsWithChildren} from 'react';
import { useCallback, useMemo } from 'react';
import { FaTimes } from 'react-icons/fa';
import type { InputFieldClearButtonProps } from './types';

export * from './types';

export const InputFieldClearButton = <TValue,>(
  props: PropsWithChildren<InputFieldClearButtonProps<TValue>>,
) => {
  const {
    hideChildrenOnShown = true,
    showOnChange = false,
    showOnNotEmpty = false,
    initialValue,
    value,
    multiple = false,
    onClear,
    componentOnHidden,
    children,
  } = props;

  const isChanged: boolean = useMemo(() => {
    return (
      JSON.stringify(value ?? (multiple ? [] : undefined)) !==
      JSON.stringify(initialValue ?? (multiple ? [] : undefined))
    );
  }, [value, initialValue, multiple]);

  const isEmpty: boolean = useMemo(() => {
    if (multiple) {
      if (!value || !(value as TValue[]).length) return true;
    }
    if (typeof value === 'string') return !value;
    return value == null;
  }, [value, multiple]);

  const show: boolean = useMemo(() => {
    if (showOnChange && isChanged) return true;
    if (showOnNotEmpty && !isEmpty) return true;
    return false;
  }, [isChanged, isEmpty, showOnChange, showOnNotEmpty]);

  const clickHandler = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      let newValue: TValue | TValue[] | undefined;
      if (showOnNotEmpty) {
        newValue = undefined;
      }
      if (showOnChange) {
        newValue = multiple ? initialValue ?? [] : (initialValue as any);
      }
      onClear?.(newValue);
    },
    [initialValue, multiple, onClear, showOnChange, showOnNotEmpty],
  );

  return (
    <>
      {!show && (
        <>
          {componentOnHidden}
          {children}
        </>
      )}
      {!!show && (
        <>
          <FaTimes onClick={clickHandler} />
          {!hideChildrenOnShown && children}
        </>
      )}
    </>
  );
};
